import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding #25: Adding MobX to a vanilla React project",
  "description": "",
  "published": "2016-10-31T00:00:00.000Z",
  "redirect_from": ["/blog/livecoding-25-adding-mobx-vanilla-react-project/swizec/7170"],
  "categories": "Front End, Livecoding, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding ?. Always under 500 words and with pictures. You can follow my channel, `}<a parentName="em" {...{
          "href": "https://livecoding.tv/swizec"
        }}>{`here`}</a>{`. New content almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{`. There’s live chat, come say hai ?`}</em></p>
    <p>{`Well, this is embarrassing… there's supposed to be an embedded video right here but… I didn't make one.`}</p>
    <p>{`After an hour of coding and talking and streaming and explaining all the things I was doing and struggling with OBS eating 300% CPU, leaving one core to almost keep up with Emacs and Chrome and Npm and JavaScript, someone said, `}<em parentName="p">{`"Errr… chatting with you is great but… uhm… no video today?"`}</em></p>
    <p>{`?`}</p>
    <p>{`No wonder the view count wasn't rising and everyone was bouncing! I had done 10 seconds of streaming, then I went blank ?`}</p>
    <p>{`But I think I came up with a generalized, albeit not fleshed out, approach to taking a vanilla React project and adding MobX or Redux for state management. You should think of the code samples in this article as pseudocode.`}</p>
    <p>{`Let's say you've been a good programmer, and you’ve followed a faux flux approach even without a state handling library. React is good at encouraging that practice. If you didn't, you're going to have problems refactoring. If you followed it loosely like I did, you're going to have problems, but not quite as many.`}</p>
    <p>{`You have a main component that is `}<em parentName="p">{`the`}</em>{` source of truth. Child components have a bit of their own state, and they use callback chains to propagate changes back up the tree.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/10/photo.png",
        "alt": null
      }}></img></p>
    <p>{`In theory, following the unidirectional dataflow paradigm: data/state flows down the tree, changes flow up the tree.`}</p>
    <p>{`This is superb for example projects and small hierarchies, but it gets real messy real fast. You lose a lot of flexibility, and passing all those callbacks around gets old fast.`}</p>
    <p>{`Oh, you want to make a global state change from a button deep down in this 10-step hierarchy? Better make all those components aware of what the tiny button is doing! Don't even think about moving it somewhere else.`}</p>
    <p>{`That's where Redux or MobX step in.`}</p>
    <p><strong parentName="p">{`First`}</strong>{`, you look at your App component's state. Let's say it has 5 important parts:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class App extends Component {
    state = {
        rawData: [],
        filteredData: [],
        filteringBy: null,
        filter: () => true
        someRandomBool: false
    };
}
`}</code></pre>
    <p>{`You can deduce your entire component tree from the value of those 5 properties. If that's not true, then your refactor will require more steps, and most of them will hurt :)`}</p>
    <p>{`Your `}<strong parentName="p">{`next step`}</strong>{` is to create a Redux or MobX store. I'm going to show you MobX because I've been enjoying it a lot lately.`}</p>
    <p>{`Looking at that `}<inlineCode parentName="p">{`state`}</inlineCode>{`, you can guess that `}<inlineCode parentName="p">{`rawData`}</inlineCode>{`, `}<inlineCode parentName="p">{`filteringBy`}</inlineCode>{`, `}<inlineCode parentName="p">{`filter`}</inlineCode>{`, and `}<inlineCode parentName="p">{`someRandomBool`}</inlineCode>{` are the state properties. `}<inlineCode parentName="p">{`filteredData`}</inlineCode>{` can be computed. It smells like `}<inlineCode parentName="p">{`filter`}</inlineCode>{` might be computable as well, but it's on the fence.`}</p>
    <p>{`In a MobX store, that looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Store {
  @observable rawData = [];
  @observable filteringBy = null;
  @observable filter = () => true;
  @observalbe someRandomBool = false;

  @computed filteredData() {
    return this.rawData.filter(this.filter);
  }
}
`}</code></pre>
    <p><inlineCode parentName="p">{`@observable`}</inlineCode>{` is a decorator that makes a variable observable. That's MobX lingo for `}<em parentName="p">{`"I want stuff to happen when this changes"`}</em>{`. `}<inlineCode parentName="p">{`@computed`}</inlineCode>{` is a decorator that makes the return value of a method observable `}<em parentName="p">{`and`}</em>{` adds memoization.`}</p>
    <p>{`So no matter how often you hit that function, it only executes when its result might change. Otherwise, it's as fast as a static value.`}</p>
    <p>{`That's why we used to put computable stuff in `}<inlineCode parentName="p">{`this.state`}</inlineCode>{`. To make things faster. I like this new approach because it tells anyone who cares what's a base state of the system and what's deducible.`}</p>
    <p><strong parentName="p">{`After that`}</strong>{`, you go back to your `}<inlineCode parentName="p">{`App`}</inlineCode>{`, replace `}<inlineCode parentName="p">{`state`}</inlineCode>{` with a `}<inlineCode parentName="p">{`store`}</inlineCode>{`, and make App an observer.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`@observer
class App extends Component {
  store = new Store();

  render() {
    // change all this.state to this.store
  }
}
`}</code></pre>
    <p>{`And you're done. Mostly.`}</p>
    <p>{`From this point onward, you should use `}<inlineCode parentName="p">{`this.store`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`this.state`}</inlineCode>{`, and you can ignore `}<inlineCode parentName="p">{`this.setState`}</inlineCode>{` in favor of good old `}<inlineCode parentName="p">{`this.store.someRandomBool = true`}</inlineCode>{`. MobX's engine will trigger a re-render on any component that is an `}<inlineCode parentName="p">{`@observer`}</inlineCode>{` and touches that value.`}</p>
    <p>{`Neat.`}</p>
    <p>{`Tomorrow, we'll talk about refactoring `}<em parentName="p">{`that`}</em>{` into actions so your business logic becomes an easy-to-understand state machine.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      